<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Workflows') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'ProjectSettings|Workflows define the lifecycle of tasks in your project. A workflow consists of statuses (like "To Do", "In Progress", "Done") and transitions that define how tasks can move between statuses.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|How It Works') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|Each workflow contains statuses that represent different stages of task completion. Transitions define which status changes are allowed. You can create multiple workflows for different types of work, and assign them to task categories.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Statuses') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|Statuses represent stages in the task lifecycle. You can mark statuses as initial (starting point), final (completion point), or require completed checklists before a task can reach that status.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Transitions') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|Transitions define how tasks can move between statuses. You can create specific transitions (from one status to another) or global transitions (from any status to a target status). Transitions can have optional names to describe the action.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Default Workflow') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|You can set one workflow as the default. Task categories that are set to auto-select will use the default workflow. This ensures consistency across tasks that do not specify a particular workflow.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Benefits') }}</h4>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Define clear processes for different types of work') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Control how tasks progress through their lifecycle') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Track progress with visual status indicators') }}
              </li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('ProjectSettings|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.info-section ul {
  line-height: 1.6;
}
</style>
